/**
 * ☰ Admin › AI — which provider writes the Summary, which Describe, and which
 * runs the chat.
 *
 * `docs/features/summary.md` and `docs/features/dsl.md`. Each choice is stored
 * once for everybody: the Summary's applies to both parts of the Summary, the
 * Describe one to English → DSL, the chat one to the QueVee agent (both default
 * DeepSeek Flash). Keys live in quevee's
 * env; this screen only says whether each is set, and a provider without one
 * cannot be picked (quevee refuses it too). Notes already cached keep the
 * provider that wrote them until their four hours run out.
 */

import { useEffect, useState } from "react";

import {
  fetchSummarySettings,
  saveChatProvider,
  saveDescribeProvider,
  saveSummaryProvider,
  type ProviderChoice,
  type SummarySettings,
  type SummarySettingsResult,
} from "../api/analyst";

const SUB: React.CSSProperties = {
  margin: "6px 0 4px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
};
const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "4px 0",
  fontSize: 13,
  flexWrap: "wrap",
};
const MODEL: React.CSSProperties = {
  fontFamily: "ui-monospace, monospace",
  fontSize: 12,
  color: "var(--qf-text-secondary)",
};
const NOTE: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 12 };
const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative, #d66)" };

export function AdminAi() {
  const [settings, setSettings] = useState<SummarySettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void fetchSummarySettings().then((result) => {
      if (result.ok) setSettings(result.settings);
      else setError(result.error);
    });
  }, []);

  const choose = async (
    current: string | undefined,
    provider: string,
    save: (provider: string) => Promise<SummarySettingsResult>,
  ) => {
    if (!settings || provider === current || saving) return;
    setSaving(true);
    setError(null);
    const result = await save(provider);
    setSaving(false);
    if (result.ok) setSettings(result.settings);
    else setError(result.error);
  };

  return (
    <div>
      <div style={SUB}>Summary model</div>
      {error && <div style={ERROR}>{error}</div>}
      {!settings && !error && <div style={MUTED}>Loading…</div>}
      {settings && (
        <>
          <Picker
            name="summary-provider"
            choice={settings}
            saving={saving}
            onChoose={(id) => void choose(settings.provider, id, saveSummaryProvider)}
          />
          <div style={{ ...NOTE, marginTop: 8 }}>
            Applies to the Summary note and its personal section. Theme classification stays on
            Anthropic. Notes already cached (up to 4 h) were written by the old provider;
            new notes use the new one.
          </div>
          <Changed choice={settings} />
        </>
      )}
      {settings?.describe && (
        <>
          <div style={{ ...SUB, marginTop: 16 }}>Describe model</div>
          <Picker
            name="describe-provider"
            choice={settings.describe}
            saving={saving}
            onChoose={(id) => void choose(settings.describe?.provider, id, saveDescribeProvider)}
          />
          <div style={{ ...NOTE, marginTop: 8 }}>
            Writes a screener column from a sentence (English → DSL). Each successful Describe is
            charged to the member's credits; a failed one is free.
          </div>
          <Changed choice={settings.describe} />
        </>
      )}
      {settings?.chat && (
        <>
          <div style={{ ...SUB, marginTop: 16 }}>Chat model</div>
          <Picker
            name="chat-provider"
            choice={settings.chat}
            saving={saving}
            onChoose={(id) => void choose(settings.chat?.provider, id, saveChatProvider)}
          />
          <div style={{ ...NOTE, marginTop: 8 }}>
            Runs the QueVee chat. Each turn is charged to the member's credits, every model call
            in it summed. A chat already open keeps the provider it started on; new chats use
            the new one.
          </div>
          <Changed choice={settings.chat} />
        </>
      )}
    </div>
  );
}

/** One provider list: a radio per provider, greyed out where its key is not set. */
function Picker({
  name,
  choice,
  saving,
  onChoose,
}: {
  name: string;
  choice: ProviderChoice;
  saving: boolean;
  onChoose: (id: string) => void;
}) {
  return (
    <>
      {choice.providers.map((p) => (
        <label
          key={p.id}
          style={{ ...ROW, cursor: p.hasKey ? "pointer" : "default", opacity: p.hasKey ? 1 : 0.5 }}
          title={p.hasKey ? undefined : `Set ${p.keyEnv} on quevee to enable`}
        >
          <input
            type="radio"
            name={name}
            checked={choice.provider === p.id}
            disabled={!p.hasKey || saving}
            onChange={() => onChoose(p.id)}
          />
          <span style={{ minWidth: 80 }}>{p.label}</span>
          <span style={MODEL}>{p.model}</span>
          <span style={MUTED}>{p.hasKey ? "key ✓" : `key ✗ (${p.keyEnv})`}</span>
        </label>
      ))}
    </>
  );
}

function Changed({ choice }: { choice: ProviderChoice }) {
  if (!choice.updatedAt) return null;
  return (
    <div style={{ ...MUTED, marginTop: 6 }}>
      Last changed {new Date(choice.updatedAt).toLocaleString()}
      {choice.updatedBy ? ` by ${choice.updatedBy}` : ""}
    </div>
  );
}
